<template>
  <cui-filter-wrapper
    class="justify-end mb-0 filter-wrapper"
    :show-clear-button="showClearButton"
    @clear-filters-clicked="emit('clear')"
  >
    <div class="devices-filters__content d-flex ga-1 align-center">
      <slot name="prepend" />

      <v-combobox
        v-model="searchModel"
        variant="outlined"
        clearable
        clear-icon="icon-close-xs"
        :items="suggestions"
        :placeholder="t('general.search')"
        :aria-label="t('general.search')"
        class="search-field"
        hide-details
        density="compact"
        rounded
        bg-color="white"
        menu-icon=""
        persistent-clear
        data-testid="tickets-page-search-field"
        :class="{ 'filter-active': searchModel }"
        :style="{ width: `${searchWidth}px`, 'max-width': `${searchWidth}px` }"
      >
      </v-combobox>

      <slot name="after-search" />

      <v-select
        v-model="protectionStatusesModel"
        :items="deviceProtectionStatusItems"
        :disabled="disabled"
        bg-color="white"
        menu-icon="icon-arrow-down-xs"
        :menu-props="{ maxHeight: '300' }"
        class="protection-status-filter cui-filter-item"
        :class="{ 'cui-filter-active': protectionStatusesModel.length }"
        :placeholder="t('devices.filters.protectionStatus')"
        data-testid="devices-page-protection-status-filter"
        density="compact"
        variant="outlined"
        multiple
        rounded
        hide-details
      >
        <template #selection="{ index }">
          <span v-if="index === 0" class="body2">{{ t("devices.filters.protectionStatus") }}</span>
        </template>
        <template #label>
          <span v-if="protectionStatusesModel.length" class="filter-label">
            {{ protectionStatusesModel.length }}
          </span>
        </template>
        <template #item="{ item, props: itemProps }">
          <v-list-item
            v-bind="itemProps"
            :title="t(`devices.protectionStatus.${item.raw}`)"
            :data-testid="`devices-page-protection-status-filter-${item.raw}-item`"
          >
            <template #prepend="{ isActive }">
              <v-list-item-action start>
                <v-checkbox-btn density="compact" :model-value="isActive"></v-checkbox-btn>
              </v-list-item-action>
            </template>
          </v-list-item>
        </template>
      </v-select>

      <device-vulnerability-filter
        v-model="vulnerabilitiesModel"
        :disabled="disabled"
        class="vulnerabilities-filter"
        :class="{ 'cui-filter-active': vulnerabilitiesModel.length }"
        data-test-id="devices-page-vulnerabilities-filter"
      />

      <cui-additional-filters-selector
        v-model="localFilters"
        :filter-keys="additionalFilterKeys"
        :default-filters="defaultFilters"
        :placeholder="t('general.filters')"
        :save-btn="t('general.save')"
        :cancel-btn="t('general.cancel')"
      >
        <template #default="{ draft }">
          <cui-filter-wrapper :render-clear-button="false">
            <div class="d-flex flex-column flex-grow-1 ga-4">
              <labels-filter
                v-model="draft.labels"
                :items="labelOnlyItems"
                :disabled="disabled"
                clearable
                :placeholder="t('devices.filters.labels')"
                :no-data-text="t('devices.filters.label.noData')"
                class="label-filter filter-menu"
                :class="{ 'cui-filter-active': draft.labels?.length }"
                data-test-id="devices-page-label-filter"
              />

              <v-select
                v-model="draft.osVersion"
                :items="osVersionItems()"
                :disabled="disabled"
                :menu-props="{ maxHeight: '300' }"
                class="os-version-filter filter-menu fit"
                :class="{ 'cui-filter-active': draft.osVersion }"
                :placeholder="t('devices.filters.osVersion')"
                data-testid="devices-page-os-version-filter"
                density="compact"
                item-value="name"
                item-title="name"
                variant="outlined"
                rounded
                hide-details
                clearable
                bg-color="white"
                @update:model-value="draft.clientVersion = null"
              >
                <template #selection="{ item }">
                  <span class="body2">
                    {{ item.title }}
                  </span>
                </template>
                <template #item="{ item, props: itemProps }">
                  <v-list-subheader v-if="item.raw.subheader" :title="item.raw.name" />
                  <v-list-item
                    v-else
                    :data-testid="`devices-page-os-version-filter-${item.raw.name}-item`"
                    v-bind="itemProps"
                  >
                  </v-list-item>
                </template>
              </v-select>

              <v-select
                v-model="draft.statuses"
                :items="deviceStatusItems"
                :disabled="disabled"
                bg-color="white"
                menu-icon="icon-arrow-down-xs"
                item-value="id"
                item-title="name"
                :menu-props="{ maxHeight: '300' }"
                class="status-filter filter-menu"
                :class="{ 'cui-filter-active': draft.statuses?.length }"
                :placeholder="t('devices.filters.status')"
                data-testid="devices-page-status-filter"
                density="compact"
                variant="outlined"
                multiple
                rounded
                hide-details
                clearable
              >
                <template #selection="{ index }">
                  <span v-if="index === 0" class="body2">{{ t("devices.filters.status") }}</span>
                </template>
                <template #label>
                  <span v-if="draft.statuses?.length" class="filter-label">
                    {{ draft.statuses.length }}
                  </span>
                </template>
                <template #item="{ item, props: itemProps }">
                  <v-list-item v-bind="itemProps" title="">
                    <template #prepend="{ isActive }">
                      <v-list-item-action start>
                        <v-checkbox-btn density="compact" :model-value="isActive"></v-checkbox-btn>
                      </v-list-item-action>
                    </template>

                    <v-list-item-title
                      :data-testid="`devices-page-status-filter-${item.raw.name}-item`"
                      class="d-flex align-center label-popup-block"
                      title=""
                    >
                      <span
                        class="color-preview mr-2"
                        :style="{ 'background-color': item.raw.color }"
                      />
                      <span>{{
                        getFilterItemDisplayName(item.raw as DeviceStatusFilterItem)
                      }}</span>
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </v-select>

              <device-coro-version-filter
                v-model="draft.clientVersion"
                :items="clientVersionItems(draft.osVersion)"
                :disabled="disabled"
                clearable
                class="agent-version-filter filter-menu fit"
                :class="{ 'cui-filter-active': draft.clientVersion }"
                data-test-id="devices-page-client-version-filter"
              />

              <v-select
                v-model="draft.agentHealth"
                :items="agentHealthItems"
                :disabled="disabled"
                bg-color="white"
                menu-icon="icon-arrow-down-xs"
                item-value="id"
                item-title="name"
                :menu-props="{ maxHeight: '300' }"
                class="agent-health-filter filter-menu"
                :class="{ 'cui-filter-active': draft.agentHealth?.length }"
                :placeholder="t('devices.filters.agentHealth')"
                data-testid="devices-page-agent-health-filter"
                density="compact"
                variant="outlined"
                multiple
                rounded
                hide-details
                clearable
              >
                <template #selection="{ index }">
                  <span v-if="index === 0" class="body2">{{
                    t("devices.filters.agentHealth")
                  }}</span>
                </template>
                <template #label>
                  <span v-if="draft.agentHealth?.length" class="filter-label">
                    {{ draft.agentHealth.length }}
                  </span>
                </template>
                <template #item="{ item, props: itemProps }">
                  <v-list-item v-bind="itemProps" title="">
                    <template #prepend="{ isActive }">
                      <v-list-item-action start>
                        <v-checkbox-btn density="compact" :model-value="isActive"></v-checkbox-btn>
                      </v-list-item-action>
                    </template>

                    <v-list-item-title
                      :data-testid="`devices-page-agent-health-filter-${item.raw.name}-item`"
                      class="d-flex align-center label-popup-block"
                      title=""
                    >
                      <span
                        class="color-preview mr-2"
                        :style="{ 'background-color': item.raw.color }"
                      />
                      <span>{{
                        getFilterItemDisplayName(item.raw as DeviceStatusFilterItem)
                      }}</span>
                    </v-list-item-title>
                  </v-list-item>
                </template>
              </v-select>

              <v-select
                v-model="draft.associated"
                :items="associationFilterItems"
                :disabled="disabled"
                item-title="text"
                item-value="value"
                :class="{ 'cui-filter-active': draft.associated !== null }"
                :placeholder="t('devices.filters.associated.title')"
                persistent-placeholder
                density="compact"
                variant="outlined"
                rounded
                hide-details
                bg-color="white"
                data-testid="devices-page-associated-filter"
                clearable
              >
                <template #item="{ item, props: itemProps }">
                  <v-list-item v-bind="itemProps" :title="item.title"></v-list-item>
                </template>
              </v-select>
            </div>
          </cui-filter-wrapper>
        </template>
      </cui-additional-filters-selector>
    </div>
  </cui-filter-wrapper>
</template>

<script setup lang="ts" generic="T extends DevicesFilters">
import { toRef, toRefs } from "vue";
import { useI18n } from "vue-i18n";
import type { DevicesFilters } from "@/_store/filters.module";
import type { DeviceOsOrClientVersions, DeviceStatusFilterItem } from "@/_store/devices.module";
import type { DeviceLabel } from "@/_store/devices-settings.module";
import { CuiAdditionalFiltersSelector, CuiFilterWrapper } from "@coronet/cui";
import { useDevicesFilters } from "@/composables/devices/useDevicesFilters";
import { useTwoWayBinding } from "@/composables/useTwoWayBinding";
import DeviceVulnerabilityFilter from "@/components/filters/DeviceVulnerabilityFilter.vue";
import LabelsFilter from "@/components/filters/LabelsFilter.vue";
import DeviceCoroVersionFilter from "@/components/filters/DeviceCoroVersionFilter.vue";

const props = withDefaults(
  defineProps<{
    modelValue: T;
    defaultFilters: T;
    labels?: DeviceLabel[];
    statuses?: DeviceStatusFilterItem[];
    rawOsVersions?: DeviceOsOrClientVersions | null;
    rawClientVersions?: DeviceOsOrClientVersions | null;
    suggestions?: string[];
    showClearButton?: boolean;
    searchWidth?: number;
    disabled?: boolean;
  }>(),
  {
    labels: () => [],
    statuses: () => [],
    rawOsVersions: () => ({}),
    rawClientVersions: () => ({}),
    suggestions: () => [],
    showClearButton: true,
    searchWidth: 245,
    disabled: false,
  }
);

const emit = defineEmits<{
  (event: "update:modelValue", value: T): void;
  (event: "clear"): void;
}>();

const { t } = useI18n();

const localFilters = useTwoWayBinding(toRef(props, "modelValue"), (value) => {
  emit("update:modelValue", value);
});

const additionalFilterKeys: (keyof DevicesFilters)[] = [
  "associated",
  "agentHealth",
  "labels",
  "osVersion",
  "clientVersion",
  "statuses",
];

const { labels, statuses, rawOsVersions, rawClientVersions } = toRefs(props);

const {
  searchModel,
  protectionStatusesModel,
  vulnerabilitiesModel,
  associationFilterItems,
  deviceStatusItems,
  agentHealthItems,
  labelOnlyItems,
  osVersionItems,
  clientVersionItems,
  deviceProtectionStatusItems,
  getFilterItemDisplayName,
} = useDevicesFilters<T>({
  modelValue: localFilters,
  emitUpdate: (value) => {
    localFilters.value = value;
  },
  labels,
  statuses,
  rawOsVersions,
  rawClientVersions,
});

defineExpose({
  deviceStatusItems,
  agentHealthItems,
  labelOnlyItems,
  associationFilterItems,
});
</script>

<style scoped lang="scss">
.devices-filters__content {
  width: 100%;
}

.vulnerabilities-filter {
  width: 160px;
  max-width: 160px;
}

:deep(*) {
  .v-chip-group .v-chip {
    min-width: 32px;
  }

  .clear-btn {
    z-index: 0 !important;
  }

  .label-popup-block span {
    flex: unset !important;
  }

  .filter-wrapper__content {
    flex-grow: 0 !important;
  }
}

.color-preview {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  border-radius: 2px !important;
}

.protection-status-filter {
  width: 180px;
}

:deep(.v-field__field > .v-field__input > :is(input, .v-select__selection)) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  min-width: 0;
  max-width: 100%;
}
</style>
